<script>
import { removeAccents } from '@utils/Format'

/**
 * Seletor de grupo familiar do consolidador.
 *
 * Gêmeo visual de `<ClientSearch>`, mas com estado ISOLADO de propósito: aquele
 * componente despacha `user/addSearchClient` e emite `changeClient`, que é o
 * contexto global de cliente consumido por todas as telas de carteira.
 * Parametrizá-lo para às vezes selecionar um grupo faria a seleção de grupo
 * vazar para as telas por cliente. Aqui o grupo sai por evento e quem decide o
 * que fazer com ele é a view.
 */
export default {
  name: 'FamilyGroupSearch',
  inheritAttrs: false,
  props: {
    value: {
      type: Object,
      default: null,
    },
    grupos: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    selectedId() {
      return this.value && this.value.id ? this.value.id : null
    },
    // O subtítulo com o número de membros é o que diferencia dois grupos de
    // nome parecido — sem ele o consultor abre o grupo errado e só descobre
    // depois de a consolidação inteira carregar.
    items() {
      return this.grupos.map((grupo) => ({
        ...grupo,
        label: grupo.grupo_familiar_nome || 'Grupo sem nome',
        subtitle: this.buildSubtitle(grupo),
        searchText: this.buildSearchText(grupo),
      }))
    },
  },
  methods: {
    buildSubtitle(grupo) {
      const total = Number(grupo.total_membros)
      if (!Number.isFinite(total) || total <= 0) return 'Sem membros vinculados'
      return total === 1 ? '1 membro' : `${total} membros`
    },
    /**
     * A busca casa também por nome de membro: o consultor lembra do cliente,
     * nem sempre do nome que deram à família.
     */
    buildSearchText(grupo) {
      const membros = Array.isArray(grupo.membros) ? grupo.membros : []
      const nomes = membros
        .filter((membro) => membro && typeof membro.nome === 'string')
        .map((membro) => membro.nome)
      const partes = [grupo.grupo_familiar_nome, grupo.descricao, ...nomes]
      return removeAccents(partes.filter(Boolean).join(' ').toLowerCase())
    },
    customFilter(item, queryText) {
      const search = removeAccents(String(queryText || '').toLowerCase())
      return item.searchText.includes(search)
    },
    handleChange(id) {
      const grupo = this.items.find((item) => item.id === id) || null
      this.$emit('input', grupo)
      if (grupo) this.$emit('select', grupo)
    },
  },
}
</script>

<template>
  <div class="family-group-search">
    <v-autocomplete
      id="family-group-search"
      v-bind="$attrs"
      :value="selectedId"
      :items="items"
      :loading="loading"
      :filter="customFilter"
      :menu-props="{ offsetY: true }"
      item-value="id"
      item-text="label"
      autocomplete="off"
      rounded
      class="p-0 new-ux-scroll family-group-search__field"
      placeholder="Selecione um grupo familiar"
      @change="handleChange"
    >
      <template v-slot:item="data">
        <v-list-item-content>
          <v-list-item-title>{{ data.item.label }}</v-list-item-title>
          <v-list-item-subtitle class="az-text-secondary">
            {{ data.item.subtitle }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </template>
    </v-autocomplete>
  </div>
</template>

<style lang="sass" scoped>
.family-group-search
  // 340px: "Familia Souza (Ribeirao da ilha)" truncava em 260px.
  max-width: 340px

.family-group-search__field
  &.v-text-field--rounded
    border-radius: var(--radius-md) !important
    ::v-deep
      fieldset
        border-radius: var(--radius-md) !important
      > .v-input__control > .v-input__slot
        border-radius: var(--radius-md) !important
        padding: 0 12px !important
</style>
